import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";
import {
  fetchCourses,
  fetchCourseTracks,
  fetchBlogPosts,
  fetchOurServices,
  fetchOurJobs,
  fetchLandingBundle,
  LandingBundleData,
} from "../../services/rootApi";
import {
  fetchEnrollmentData,
  fetchEnrollmentStatus,
  fetchPaymentHistory,
  fetchTrackDetailBySlug,
  fetchCourseDetailBySlug,
  handleCourseTrackEnrollment,
} from "../../services/apiEndpoints";

/**
 * Hook to fetch all courses list with automatic memory cache
 */
export const useCoursesQuery = () => {
  return useQuery({
    queryKey: queryKeys.courses.list(),
    queryFn: ({ signal }) => fetchCourses(signal),
  });
};

/**
 * Hook to fetch all career tracks
 */
export const useCourseTracksQuery = () => {
  return useQuery({
    queryKey: queryKeys.tracks.list(),
    queryFn: ({ signal }) => fetchCourseTracks(signal),
  });
};

/**
 * Hook to fetch blog posts
 */
export const useBlogPostsQuery = () => {
  return useQuery({
    queryKey: queryKeys.blog.list(),
    queryFn: ({ signal }) => fetchBlogPosts(signal),
  });
};

/**
 * Hook to fetch our services
 */
export const useOurServicesQuery = () => {
  return useQuery({
    queryKey: queryKeys.services.list(),
    queryFn: ({ signal }) => fetchOurServices(signal),
  });
};

/**
 * Hook to fetch our jobs
 */
export const useOurJobsQuery = () => {
  return useQuery({
    queryKey: queryKeys.jobs.list(),
    queryFn: ({ signal }) => fetchOurJobs(signal),
  });
};

/**
 * Hook to fetch landing & company info bundle
 */
export const useLandingBundleQuery = () => {
  return useQuery<LandingBundleData>({
    queryKey: queryKeys.landingBundle(),
    queryFn: ({ signal }) => fetchLandingBundle(signal),
  });
};

/**
 * Hook to fetch authenticated user's enrollment data
 */
export const useEnrollmentsQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.enrollments.list(),
    queryFn: ({ signal }) => fetchEnrollmentData(signal),
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook to fetch verification/enrollment status for a specific course or track
 */
export const useEnrollmentStatusQuery = (
  params: { courseId?: string | number; trackId?: string | number },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: queryKeys.enrollments.status(params),
    queryFn: ({ signal }) =>
      fetchEnrollmentStatus({ ...params, signal }),
    enabled: (options?.enabled ?? true) && Boolean(params.courseId || params.trackId),
  });
};

/**
 * Hook to fetch track detail by slug
 */
export const useTrackDetailQuery = (slug?: string) => {
  return useQuery({
    queryKey: queryKeys.tracks.detail(slug || ""),
    queryFn: ({ signal }) => fetchTrackDetailBySlug(slug!, signal),
    enabled: Boolean(slug),
  });
};

/**
 * Hook to fetch course detail by slug
 */
export const useCourseDetailQuery = (slug?: string, isTrack: boolean = false) => {
  return useQuery({
    queryKey: isTrack
      ? queryKeys.tracks.detail(slug || "")
      : queryKeys.courses.detail(slug || ""),
    queryFn: ({ signal }) => fetchCourseDetailBySlug(slug!, isTrack, signal),
    enabled: Boolean(slug),
  });
};

/**
 * Hook to fetch user payment history
 */
export const usePaymentHistoryQuery = () => {
  return useQuery({
    queryKey: queryKeys.payments.history(),
    queryFn: ({ signal }) => fetchPaymentHistory(signal),
  });
};

/**
 * Mutation hook for enrolling into a course or track
 * Automatically invalidates active enrollment queries on completion
 */
export const useEnrollMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, trackId }: { courseId?: any; trackId?: any }) =>
      handleCourseTrackEnrollment({ courseId, trackId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
    },
  });
};
